import { Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { Subject, takeUntil } from "rxjs";
import Swal from "sweetalert2";

import { AddPaymentComponent } from "../../../shared/add-payment/add-payment.component";
import {
  PaymentCard,
  PaymentCardService,
} from "../../../shared/services/payment-card.service";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { environment } from "../../../../environments/environment";

@Component({
  selector: "customer-account-settings",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    LoaderComponent,
  ],
  templateUrl: "./account-settings.component.html",
  styleUrl: "./account-settings.component.scss",
})
export class CustomerAccountSettingsComponent implements OnInit, OnDestroy {
  accountForm: FormGroup;

  cards: PaymentCard[] = [];
  isLoading = false;
  errorMessage = "";

  assetUrl = environment.assetBaseUrl;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private paymentCardService: PaymentCardService,
  ) {
    this.accountForm = this.fb.group({
      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      phone: [""],
      birthday: [""],
      preferredContactMethod: ["email"],
      preferredCity: [""],
      preferredService: [""],
      notes: [""],

      appointmentReminders: [true],
      promoEmails: [true],
      smsUpdates: [false],
      reviewRequests: [true],

      currentPassword: [""],
      newPassword: ["", [Validators.minLength(8)]],
      confirmPassword: [""],
    });
  }

  ngOnInit(): void {
    this.loadCards();

    this.paymentCardService.cards$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cards: PaymentCard[]) => {
        this.cards = cards;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get profileCompletion(): number {
    const requiredFields = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "birthday",
      "preferredContactMethod",
      "preferredCity",
      "preferredService",
    ];

    const completed = requiredFields.filter((fieldName) => {
      const value = this.accountForm.get(fieldName)?.value;
      return (
        value !== null && value !== undefined && String(value).trim() !== ""
      );
    }).length;

    return Math.round((completed / requiredFields.length) * 100);
  }

  get enabledNotificationCount(): number {
    return [
      "appointmentReminders",
      "promoEmails",
      "smsUpdates",
      "reviewRequests",
    ].filter((fieldName) => !!this.accountForm.get(fieldName)?.value).length;
  }

  get primaryCardLabel(): string {
    const primary = this.cards.find((card) => card.is_primary);

    if (!primary) return "No primary payment method selected.";

    return `${this.getCardBrand(primary.card_number)} ending in ${primary.card_number.slice(-4)}`;
  }

  get securityStatus(): string {
    return this.accountForm.get("newPassword")?.value ? "Updating" : "Good";
  }

  get passwordMismatch(): boolean {
    const newPassword = this.accountForm.get("newPassword")?.value;
    const confirmPassword = this.accountForm.get("confirmPassword")?.value;

    if (!newPassword && !confirmPassword) return false;

    return newPassword !== confirmPassword;
  }

  loadCards(): void {
    this.isLoading = true;
    this.errorMessage = "";

    this.paymentCardService
      .getAllCards()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cards: PaymentCard[]) => {
          this.cards = cards;
          this.isLoading = false;
        },
        error: (error: Error) => {
          this.errorMessage = error.message;
          this.isLoading = false;
          console.error("Failed to load cards:", error);
        },
      });
  }

  AddPayment = (): void => {
    const dialogRef = this.dialog.open(AddPaymentComponent, {
      width: "500px",
      disableClose: false,
      data: { card: null, mode: "add" },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        this.loadCards();
      }
    });
  };

  editCard(card: PaymentCard): void {
    const dialogRef = this.dialog.open(AddPaymentComponent, {
      width: "500px",
      disableClose: false,
      data: { card: { ...card }, mode: "edit" },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        this.loadCards();
      }
    });
  }

  setPrimaryCard(card: PaymentCard): void {
    if (card.is_primary || !card.id) return;

    this.paymentCardService
      .setPrimaryCard(card.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadCards();
        },
        error: (error: Error) => {
          this.errorMessage = error.message;
          console.error("Failed to set primary card:", error);
        },
      });
  }

  deleteCard(card: PaymentCard): void {
    if (!card.id) return;

    Swal.fire({
      title: "Delete Card?",
      text: `Are you sure you want to delete card ending in ${card.card_number.slice(-4)}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed && card.id) {
        this.paymentCardService
          .deleteCard(card.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loadCards();
            },
            error: (error: Error) => {
              this.errorMessage = error.message;
              console.error("Failed to delete card:", error);
            },
          });
      }
    });
  }

  checkValidField(fieldName: string): boolean {
    const field = this.accountForm.get(fieldName);

    if (!field) return true;

    return !(field.invalid && field.touched);
  }

  getMaskedCardNumber(cardNumber: string): string {
    return this.paymentCardService.getMaskedCardNumber(cardNumber);
  }

  getCardBrand(cardNumber: string): string {
    return this.paymentCardService.getCardBrand(cardNumber);
  }

  getCardIcon(cardNumber: string): string {
    const brand = this.getCardBrand(cardNumber);

    switch (brand) {
      case "visa":
        return this.assetUrl + "img/visa.png";
      case "mastercard":
        return this.assetUrl + "icons/master.svg";
      case "amex":
        return this.assetUrl + "icons/amex.svg";
      case "discover":
        return this.assetUrl + "icons/discover.svg";
      case "jcb":
        return this.assetUrl + "icons/jcb.svg";
      case "diners":
        return this.assetUrl + "icons/diners.svg";
      case "maestro":
        return this.assetUrl + "icons/maestro.svg";
      default:
        return this.assetUrl + "icons/credit-card.svg";
    }
  }

  saveChanges(): void {
    this.accountForm.markAllAsTouched();

    if (this.accountForm.invalid || this.passwordMismatch) {
      Swal.fire({
        title: "Check Required Fields",
        text: "Please review the highlighted fields before saving.",
        icon: "warning",
        timer: 4000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
      return;
    }

    const payload = this.accountForm.getRawValue();

    console.log("Customer account settings payload:", payload);

    // Swal.fire({
    //   title: "Success!",
    //   text: "Changes saved successfully",
    //   icon: "success",
    //   timer: 2000,
    //   showConfirmButton: false,
    //   toast: true,
    //   position: "top-end",
    // });
  }

  goBack(): void {
    window.history.back();
  }
}
